import { MedewerkerType } from "../enums";
import { fetchFout } from "../utils/foutmelding";

const API_BASE_URL = process.env.REACT_APP_API_URL;

// --- Interfaces ---

export interface DossierEmployee {
  id: string;
  dossierId: string;
  medewerkerId: string;
  rol: MedewerkerType;
  /** Alleen aanwezig voor management-/financiële rollen; anders weggelaten. */
  uren?: number;
  startDatum: Date;
  eindDatum?: Date;
  /**
   * Alleen bij mutaties: de toewijzing is opgeslagen, maar de kosten-/
   * opbrengst-sync kon niet draaien (bv. afgesloten dossier).
   */
  waarschuwing?: string;
  medewerker: {
    id: string;
    naam: string;
    voornaam?: string;
    achternaam: string;
    initialen?: string;
    /** Alleen in de lijst per dossier: false = uit dienst. */
    actief?: boolean;
  };
}

export interface CreateDossierEmployeeRequest {
  medewerkerId: string;
  rol: MedewerkerType;
  uren?: number;
  startDatum?: string;
}

export interface UpdateDossierEmployeeRequest {
  uren?: number;
  startDatum?: string; // NEW
  eindDatum?: string;
}

// --- Default Hours (matching backend) ---

export const ROLE_DEFAULT_HOURS: Record<MedewerkerType, number> = {
  [MedewerkerType.ARTS]: 5.0,
  [MedewerkerType.ASSISTENT]: 9.0,
  [MedewerkerType.WAARNEMEND_ASSISTENT]: 0.5,
  [MedewerkerType.VOORBEREIDINGS_ASSISTENT]: 3.0,
  [MedewerkerType.RADIOLOGIE_ASSISTENT]: 0,
  [MedewerkerType.PM]: 1.0,
  [MedewerkerType.INPLANNER]: 1.0,
  [MedewerkerType.MANAGER]: 0.0,
  [MedewerkerType.FINANCIELE_MANAGER]: 0.0,
  [MedewerkerType.ADMIN]: 0.0,
  [MedewerkerType.OVERIG]: 0.0,
  [MedewerkerType.WEBINAR_MANAGER]: 0.0,
};

// --- Helper Functions ---

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    // Status en antwoord gaan mee in de fout (B12), voor maakFoutmelding.
    const data = await response.json().catch(() => null);
    throw fetchFout(response, data, `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// --- API Functions ---

/**
 * Fetches all employees linked to a dossier (both active and inactive)
 */
export async function getDossierEmployees(
  dossierKenmerk: string,
): Promise<DossierEmployee[]> {
  const response = await fetch(
    `${API_BASE_URL}/dossiers/${dossierKenmerk}/employees`,
    {
      credentials: "include",
    },
  );
  return handleResponse<DossierEmployee[]>(response);
}

/**
 * Adds an employee to a dossier
 */
export async function addDossierEmployee(
  dossierKenmerk: string,
  data: CreateDossierEmployeeRequest,
): Promise<DossierEmployee> {
  const response = await fetch(
    `${API_BASE_URL}/dossiers/${dossierKenmerk}/employees`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    },
  );
  return handleResponse<DossierEmployee>(response);
}

/**
 * Updates an employee assignment (hours or end date)
 */
export async function updateDossierEmployee(
  dossierKenmerk: string,
  assignmentId: string,
  data: UpdateDossierEmployeeRequest,
): Promise<DossierEmployee> {
  const response = await fetch(
    `${API_BASE_URL}/dossiers/${dossierKenmerk}/employees/${assignmentId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    },
  );
  return handleResponse<DossierEmployee>(response);
}

/**
 * Removes an employee from a dossier (soft delete)
 */
export async function removeDossierEmployee(
  dossierKenmerk: string,
  assignmentId: string,
): Promise<{ message: string; waarschuwing?: string }> {
  const response = await fetch(
    `${API_BASE_URL}/dossiers/${dossierKenmerk}/employees/${assignmentId}`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );
  return handleResponse<{ message: string }>(response);
}

/**
 * Permanently removes an employee from a dossier (hard delete)
 */
export async function permanentlyDeleteDossierEmployee(
  dossierKenmerk: string,
  assignmentId: string,
): Promise<{ message: string; waarschuwing?: string }> {
  const response = await fetch(
    `${API_BASE_URL}/dossiers/${dossierKenmerk}/employees/${assignmentId}/permanent`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );
  return handleResponse<{ message: string }>(response);
}

/**
 * Reactivates an inactive employee (removes end date)
 */
export async function reactivateDossierEmployee(
  dossierKenmerk: string,
  assignmentId: string,
): Promise<DossierEmployee> {
  const response = await fetch(
    `${API_BASE_URL}/dossiers/${dossierKenmerk}/employees/${assignmentId}/reactivate`,
    {
      method: "PATCH",
      credentials: "include",
    },
  );
  return handleResponse<DossierEmployee>(response);
}

// --- Helper Functions ---

/**
 * Splits employees into active and inactive groups
 */
export function splitEmployeesByStatus(employees: DossierEmployee[]): {
  active: DossierEmployee[];
  inactive: DossierEmployee[];
} {
  const now = new Date();

  return employees.reduce(
    (acc, employee) => {
      const isInactive =
        employee.eindDatum && new Date(employee.eindDatum) <= now;
      if (isInactive) {
        acc.inactive.push(employee);
      } else {
        acc.active.push(employee);
      }
      return acc;
    },
    { active: [] as DossierEmployee[], inactive: [] as DossierEmployee[] },
  );
}

/**
 * Gets the display name for a role
 */
export function getRoleDisplayName(role: MedewerkerType): string {
  const roleNames: Record<MedewerkerType, string> = {
    [MedewerkerType.ARTS]: "Arts",
    [MedewerkerType.ASSISTENT]: "Assistent",
    [MedewerkerType.WAARNEMEND_ASSISTENT]: "Waarnemend Assistent",
    [MedewerkerType.VOORBEREIDINGS_ASSISTENT]: "Voorbereidings Assistent",
    [MedewerkerType.RADIOLOGIE_ASSISTENT]: "Radiologie Assistent",
    [MedewerkerType.PM]: "Project Manager",
    [MedewerkerType.INPLANNER]: "Inplanner",
    [MedewerkerType.MANAGER]: "Manager",
    [MedewerkerType.FINANCIELE_MANAGER]: "Financiële Manager",
    [MedewerkerType.ADMIN]: "Admin",
    [MedewerkerType.OVERIG]: "Overig",
    [MedewerkerType.WEBINAR_MANAGER]: "Webinarmanager",
  };
  return roleNames[role] || role;
}
